import { bigintToFieldHex, merkleHash, parseField } from "./field";

export const V2_TREE_DEPTH = 32;
const ZERO = bigintToFieldHex(0n);

export type MerkleTree = {
  root: string;
  path(index: number): { pathElements: string[]; pathIndices: string[] };
};

type FrontierNode = { hash: string; targets: number[] };

export type MerkleAccumulator = {
  append(commitment: string, track?: boolean): Promise<number>;
  finalize(): Promise<string>;
  path(index: number): { pathElements: string[]; pathIndices: string[] };
  readonly length: number;
};

/**
 * Rebuilds the contract root without retaining the complete public history.
 * Only paths explicitly marked with `track` are retained, making total-loss
 * recovery O(tree depth + owned notes) memory rather than O(all chain notes).
 */
export const createMerkleAccumulator = async (): Promise<MerkleAccumulator> => {
  const zeroes = [ZERO];
  for (let level = 0; level < V2_TREE_DEPTH; level += 1) {
    zeroes.push(await merkleHash(zeroes[level], zeroes[level]));
  }
  const frontier: Array<FrontierNode | undefined> = Array(V2_TREE_DEPTH);
  const paths = new Map<
    number,
    { pathElements: string[]; pathIndices: string[] }
  >();
  let count = 0;
  let finalized: string | undefined;

  const merge = async (
    left: FrontierNode,
    right: FrontierNode,
    level: number
  ): Promise<FrontierNode> => {
    for (const target of left.targets) {
      paths.get(target)!.pathElements[level] = right.hash;
    }
    for (const target of right.targets) {
      paths.get(target)!.pathElements[level] = left.hash;
    }
    return {
      hash: await merkleHash(left.hash, right.hash),
      targets: left.targets.concat(right.targets),
    };
  };

  return {
    get length() {
      return count;
    },
    async append(commitment, track = false) {
      if (finalized !== undefined)
        throw new Error("Cannot append after finalizing a Merkle recovery");
      if (count >= 2 ** V2_TREE_DEPTH)
        throw new Error("The Private Reserve tree is full");
      parseField(commitment);
      const index = count;
      count += 1;
      if (track) {
        paths.set(index, {
          pathElements: zeroes.slice(0, V2_TREE_DEPTH),
          pathIndices: Array.from({ length: V2_TREE_DEPTH }, (_, level) =>
            (Math.floor(index / 2 ** level) % 2).toString()
          ),
        });
      }
      let node: FrontierNode = {
        hash: commitment,
        targets: track ? [index] : [],
      };
      let level = 0;
      while (frontier[level]) {
        node = await merge(frontier[level]!, node, level);
        frontier[level] = undefined;
        level += 1;
      }
      frontier[level] = node;
      return index;
    },
    async finalize() {
      if (finalized !== undefined) return finalized;
      if (count === 0) {
        finalized = zeroes[V2_TREE_DEPTH];
        return finalized;
      }
      let current: FrontierNode | undefined;
      let currentLevel = 0;
      for (let level = 0; level < V2_TREE_DEPTH; level += 1) {
        while (current && currentLevel < level) {
          current = await merge(
            current,
            { hash: zeroes[currentLevel], targets: [] },
            currentLevel
          );
          currentLevel += 1;
        }
        const completed = frontier[level];
        if (completed) {
          if (current) {
            current = await merge(completed, current, level);
            currentLevel = level + 1;
          } else {
            current = completed;
            currentLevel = level;
          }
        }
      }
      if (!current) throw new Error("Unable to finalize Merkle recovery");
      while (currentLevel < V2_TREE_DEPTH) {
        current = await merge(
          current,
          { hash: zeroes[currentLevel], targets: [] },
          currentLevel
        );
        currentLevel += 1;
      }
      finalized = current.hash;
      return finalized;
    },
    path(index) {
      const result = paths.get(index);
      if (!result) throw new Error("Merkle leaf was not selected for recovery");
      return {
        pathElements: [...result.pathElements],
        pathIndices: [...result.pathIndices],
      };
    },
  };
};

export const buildMerkleTree = async (
  commitments: readonly string[]
): Promise<MerkleTree> => {
  if (commitments.length >= 2 ** V2_TREE_DEPTH)
    throw new Error("The Private Reserve tree is full");
  commitments.forEach(parseField);

  const zeroes = [ZERO];
  for (let level = 0; level < V2_TREE_DEPTH; level += 1) {
    zeroes.push(await merkleHash(zeroes[level], zeroes[level]));
  }

  const levels: string[][] = [Array.from(commitments)];
  for (let level = 0; level < V2_TREE_DEPTH; level += 1) {
    const current = levels[level];
    const parents: string[] = [];
    const parentCount = Math.max(1, Math.ceil(current.length / 2));
    for (let parent = 0; parent < parentCount; parent += 1) {
      const left = current[parent * 2] ?? zeroes[level];
      const right = current[parent * 2 + 1] ?? zeroes[level];
      parents.push(await merkleHash(left, right));
    }
    levels.push(parents);
  }

  return {
    root: levels[V2_TREE_DEPTH][0] ?? zeroes[V2_TREE_DEPTH],
    path(index: number) {
      if (
        !Number.isInteger(index) ||
        index < 0 ||
        index >= commitments.length
      ) {
        throw new Error("Merkle leaf index is out of range");
      }
      const pathElements: string[] = [];
      const pathIndices: string[] = [];
      let cursor = index;
      for (let level = 0; level < V2_TREE_DEPTH; level += 1) {
        const isRight = cursor % 2;
        pathIndices.push(isRight.toString());
        pathElements.push(
          levels[level][isRight ? cursor - 1 : cursor + 1] ?? zeroes[level]
        );
        cursor = Math.floor(cursor / 2);
      }
      return { pathElements, pathIndices };
    },
  };
};
